import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { canAccessPath } from "../utils/routeAccess";

const LAST_ALLOWED_KEY = "scm:lastAllowedPath";

function readLastAllowedPath() {
  try {
    const stored = sessionStorage.getItem(LAST_ALLOWED_KEY);
    if (stored && stored.startsWith("/")) return stored;
  } catch {
    // ignore
  }
  return "/dashboard";
}

function writeLastAllowedPath(path) {
  try {
    if (path && path.startsWith("/")) {
      sessionStorage.setItem(LAST_ALLOWED_KEY, path);
    }
  } catch {
    // ignore
  }
}

export function clearLastAllowedPath() {
  try {
    sessionStorage.removeItem(LAST_ALLOWED_KEY);
  } catch {
    // ignore
  }
}

/**
 * Blocks direct URL access when moduleAccess denies the required action.
 * Logged-in users return to their last allowed page + toast.
 * Guests / logout: no permission toast — ProtectedRoute sends them to Login.
 */
const ModuleAccessGuard = ({ children }) => {
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const lastDeniedPath = useRef("");

  const userId = user?.id || localStorage.getItem("id");
  const isLoggedIn = Boolean(userId);
  const allowed =
    !isLoggedIn || canAccessPath(user, location.pathname);
  const currentFullPath = `${location.pathname}${location.search || ""}`;

  if (isLoggedIn && allowed) {
    writeLastAllowedPath(currentFullPath);
  }

  useEffect(() => {
    // Logout / guest: never show permission toast
    if (!isLoggedIn) {
      lastDeniedPath.current = "";
      return;
    }

    if (allowed) {
      lastDeniedPath.current = "";
      return;
    }

    if (lastDeniedPath.current === location.pathname) return;
    lastDeniedPath.current = location.pathname;

    toast.error("You don't have permission to access that page", {
      autoClose: 2500,
    });
  }, [allowed, location.pathname, isLoggedIn]);

  // After logout, Layout may still be mounted briefly — pass through; ProtectedRoute → Login
  if (!isLoggedIn) {
    return children;
  }

  if (!allowed) {
    const fallback = readLastAllowedPath();
    const fallbackPath = fallback.split("?")[0];
    const target =
      !fallbackPath || fallbackPath === location.pathname
        ? "/dashboard"
        : fallback;
    return <Navigate to={target} replace />;
  }

  return children;
};

export default ModuleAccessGuard;
